import React, { useState, useContext, useEffect, useCallback } from 'react';
import AppContext from "../contexts/AppContext";
import ConfirmModal from '../components/utils/ConfirmModal';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_FOLLOWER_PERMISSION_IDS = [600, 700];

const TARGET_TYPE_LABELS = {
  venue: 'Venue',
  adv: 'Advertisement',
  ts_venue: 'Touchscreen Venue',
  ts_adv: 'Touchscreen Advertisement',
  ts_content: 'Touchscreen Content',
};

const ROLE_TYPE_TO_TARGET_TYPE = {
  venue: 'venue',
  adv: 'adv',
  tsVenue: 'ts_venue',
  tsAdv: 'ts_adv',
  tsContent: 'ts_content',
};

const normalizePermissionIds = (permissionIds) => (
  Array.isArray(permissionIds) && permissionIds.length ? permissionIds : DEFAULT_FOLLOWER_PERMISSION_IDS
);

const AddUser = () => {
  const { token, userInfo, selectedRole } = useContext(AppContext);
  const canManageFollowers = !!userInfo?.user && !userInfo.user.managedBy;
  const [followers, setFollowers] = useState([]);
  const [newFollowerEmail, setNewFollowerEmail] = useState('');
  const [newFollowerName, setNewFollowerName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [isCheckModalOpen, setIsCheckModalOpen] = useState(false);
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addChecks, setAddChecks] = useState({
    venue: false,
    contact: false,
    email: false,
  });

  const [isRemoveAssignmentModalOpen, setIsRemoveAssignmentModalOpen] = useState(false);
  const [pendingRemoveAssignment, setPendingRemoveAssignment] = useState(null);
  const [isGrantAssignmentModalOpen, setIsGrantAssignmentModalOpen] = useState(false);
  const [pendingGrantFollower, setPendingGrantFollower] = useState(null);

  // Scope the new follower to EXACTLY the one venue/adv/etc. currently
  // selected - nothing else. This used to fall back to granting access to
  // EVERY venue/adv the master user manages whenever no specific role was
  // selected, which silently attached a new user to unrelated accounts with
  // no warning (this is exactly how a GM meant for one hotel ended up
  // showing under a completely different one). Returns null when there's no
  // unambiguous single target, so the caller can block submission instead of
  // guessing a scope.
  const buildAssignments = () => {
    const targetType = selectedRole?.id && selectedRole?.type
      ? ROLE_TYPE_TO_TARGET_TYPE[selectedRole.type]
      : null;
    if (!targetType) return null;
    return [{ targetType, targetId: selectedRole.id }];
  };

  // Human-readable label for whatever buildAssignments() is currently
  // scoped to, so the confirmation dialog can show a real name instead of
  // just the new user's email - the actual account/venue name is what lets
  // someone catch "wrong venue selected" before it's too late.
  const selectedTargetLabel = (() => {
    if (!selectedRole?.id || !selectedRole?.type) return null;
    const roleList = userInfo?.roleInfo?.[selectedRole.type];
    const match = Array.isArray(roleList) ? roleList.find((r) => r.id === selectedRole.id) : null;
    return match?.name || match?.label || selectedRole.id;
  })();

  const currentScopeAssignment = buildAssignments()?.[0] || null;
  const trimmedFollowerEmail = newFollowerEmail.trim().toLowerCase();
  const trimmedFollowerName = newFollowerName.trim();
  const addConfirmReady = addChecks.venue && addChecks.contact && addChecks.email;

  const followerHasCurrentScope = (follower) => {
    if (!currentScopeAssignment || !Array.isArray(follower?.assignments)) return false;
    return follower.assignments.some((assignment) => (
      assignment.targetType === currentScopeAssignment.targetType &&
      assignment.targetId === currentScopeAssignment.targetId
    ));
  };
  const fetchFollowers = useCallback(async () => {
    if (!canManageFollowers) {
      setFollowers([]);
      setLoading(false);
      setError('Only primary accounts can manage follower users.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/user/follower/list`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch followers');
      const data = await response.json();
      setFollowers(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canManageFollowers, token]);

  useEffect(() => {
    if (!userInfo?.user) return;
    fetchFollowers();
  }, [fetchFollowers, userInfo?.user]);

  /**
   * body: {
   *   followerEmail: 'xxx',
   *   followerAccountName: 'XXX',
   *   assignments: [ { targetType, targetId }, ... ]
   * }
   */
  const addFollower = async () => {
    const trimmedEmail = trimmedFollowerEmail;
    const trimmedName = trimmedFollowerName;

    if (!trimmedEmail || !trimmedName) return;
    if (!canManageFollowers) {
      setError('Only primary accounts can manage follower users.');
      return;
    }
    const assignments = buildAssignments();
    if (!assignments) {
      setError('Select a specific venue/account before adding a user - nothing was added.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        followerEmail: trimmedEmail,
        followerAccountName: trimmedName,
        permissionIds: DEFAULT_FOLLOWER_PERMISSION_IDS,
        assignments
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/user/follower/add`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );
      if (!response.ok) {
        const errRes = await response.json();
        throw new Error(errRes.error || errRes.message || 'Failed to add follower');
      }
      setNewFollowerName('');
      setNewFollowerEmail('');
      fetchFollowers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * body: { followerEmail: 'xxx' }
   */
  const deleteFollower = async (userEmail) => {
    if (!canManageFollowers) {
      setError('Only primary accounts can manage follower users.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        followerEmail: userEmail
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/user/follower/delete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );
      if (!response.ok) {
        const errRes = await response.json();
        throw new Error(errRes.error || errRes.message || 'Failed to delete follower');
      }
      fetchFollowers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const requestRemoveAssignment = (follower, assignment) => {
    setPendingRemoveAssignment({ follower, assignment });
    setIsRemoveAssignmentModalOpen(true);
  };

  const requestGrantCurrentAssignment = (follower) => {
    if (!canManageFollowers) {
      setError('Only primary accounts can manage follower users.');
      return;
    }
    if (!currentScopeAssignment || !selectedTargetLabel) {
      setError('Select a specific venue/account before granting access.');
      return;
    }
    if (followerHasCurrentScope(follower)) {
      setError(`${follower.email} already has access to "${selectedTargetLabel}".`);
      return;
    }
    setError('');
    setPendingGrantFollower(follower);
    setIsGrantAssignmentModalOpen(true);
  };

  // Reuses the same /follower/edit endpoint to append the CURRENT selected
  // account onto an existing follower. This is the missing recovery path for
  // followers that were intentionally kept but currently have zero assignments.
  const confirmGrantCurrentAssignment = async () => {
    if (!pendingGrantFollower || !currentScopeAssignment) return;
    if (!canManageFollowers) {
      setError('Only primary accounts can manage follower users.');
      return;
    }
    const follower = pendingGrantFollower;
    setIsGrantAssignmentModalOpen(false);
    setPendingGrantFollower(null);
    setLoading(true);
    setError('');
    try {
      const existingAssignments = Array.isArray(follower.assignments)
        ? follower.assignments.map((a) => ({ targetType: a.targetType, targetId: a.targetId }))
        : [];
      const nextAssignments = [...existingAssignments, currentScopeAssignment]
        .filter((assignment, index, arr) => arr.findIndex((candidate) => (
          candidate.targetType === assignment.targetType && candidate.targetId === assignment.targetId
        )) === index);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/user/follower/edit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            followerEmail: follower.email,
            followerAccountName: follower.accountName || follower.AccountName,
            permissionIds: normalizePermissionIds(follower.permissionIds),
            assignments: nextAssignments,
          })
        }
      );
      if (!response.ok) {
        const errRes = await response.json();
        throw new Error(errRes.error || errRes.message || 'Failed to grant account access');
      }
      fetchFollowers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  // Reuses the existing /follower/edit endpoint (originally built for the
  // Categories-style "replace all assignments" edit) to drop just ONE
  // assignment, keeping every other account this follower has access to
  // untouched - this is the missing middle ground between "do nothing" and
  // "delete their whole account", which is what turned a one-venue mistake
  // into losing a legitimate assignment too.
  const confirmRemoveAssignment = async () => {
    if (!pendingRemoveAssignment) return;
    if (!canManageFollowers) {
      setError('Only primary accounts can manage follower users.');
      return;
    }
    const { follower, assignment } = pendingRemoveAssignment;
    setIsRemoveAssignmentModalOpen(false);
    setPendingRemoveAssignment(null);
    setLoading(true);
    setError('');
    try {
      const remainingAssignments = follower.assignments
        .filter((a) => !(a.targetType === assignment.targetType && a.targetId === assignment.targetId))
        .map((a) => ({ targetType: a.targetType, targetId: a.targetId }));

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/private/user/follower/edit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            followerEmail: follower.email,
            followerAccountName: follower.accountName || follower.AccountName,
            permissionIds: normalizePermissionIds(follower.permissionIds),
            assignments: remainingAssignments,
          })
        }
      );
      if (!response.ok) {
        const errRes = await response.json();
        throw new Error(errRes.error || errRes.message || 'Failed to remove assignment');
      }
      fetchFollowers();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    const trimmedEmail = trimmedFollowerEmail;
    const trimmedName = trimmedFollowerName;

    if (!canManageFollowers) {
      setError('Only primary accounts can manage follower users.');
      return;
    }

    if (!trimmedEmail || !trimmedName) {
      setError("Please enter email and name.");
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!buildAssignments()) {
      setError("Select a specific venue/account before adding a user.");
      return;
    }

    setError('');
    setAddChecks({ venue: false, contact: false, email: false });
    setIsAddModalOpen(true);
  };

  const confirmAddFollower = () => {
    setIsAddModalOpen(false);
    addFollower();
  };

  return (
    <div className="w-[90%] max-w-7xl mx-auto pt-3">
      {error && <p className="text-red-500 mb-4">{error}</p>}
      {!canManageFollowers && userInfo?.user ? (
        <div className="mt-4 rounded-[28px] border border-[#e2e7f6] bg-white px-6 py-6 text-[0.95rem] text-[#4d5b75] shadow-[0_18px_42px_rgba(15,23,42,0.05)]">
          Only primary accounts can manage follower users from this page.
        </div>
      ) : loading ? (
        <p className="text-blue-500">Loading...</p>
      ) : (
        <div className="mt-4 rounded-[28px] border border-[#e2e7f6] bg-white shadow-[0_18px_42px_rgba(15,23,42,0.05)]">
          <div className="px-6 py-5 border-t border-gray-100">
            <h2 className="text-xl font-semibold mb-2">Add</h2>
            <div className="flex space-x-2">
              <input
                type="email"
                value={newFollowerEmail}
                onChange={(e) => setNewFollowerEmail(e.target.value)}
                placeholder="Enter user email"
                className="flex-1 rounded-xl border border-[#d9e2f6] px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.04)] transition-all duration-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={40}
              />
              <input
                type="text"
                value={newFollowerName}
                onChange={(e) => setNewFollowerName(e.target.value)}
                placeholder="Enter user name"
                className="flex-1 rounded-xl border border-[#d9e2f6] px-4 py-3 shadow-[0_8px_18px_rgba(15,23,42,0.04)] transition-all duration-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={30}
              />
              <button
                onClick={handleAddClick}
                className="rounded-xl bg-blue-500 px-5 py-3 text-white shadow-[0_12px_24px_rgba(37,99,235,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-600 hover:shadow-[0_18px_30px_rgba(37,99,235,0.28)]"
              >
                Add
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <h2 className="text-xl font-semibold">User List</h2>
          </div>
          <ul className="space-y-3 px-6 py-4">
            {followers && followers.length > 0 ? (
              followers.map((follower) => (
                <li
                  key={follower.email}
                  className="rounded-2xl border border-[#e7ecf8] bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] px-4 py-3 shadow-[0_10px_22px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_16px_28px_rgba(30,52,120,0.07)]"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex-1 text-[0.95rem] text-[#24324d]">{follower.email}</span>
                    <span className="flex-1 text-[0.95rem] font-medium text-[#24324d]">{follower.AccountName || follower.accountName}</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => requestGrantCurrentAssignment(follower)}
                        disabled={!currentScopeAssignment || followerHasCurrentScope(follower)}
                        className="rounded-xl bg-emerald-500 px-4 py-2 text-white shadow-[0_10px_20px_rgba(16,185,129,0.16)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-emerald-600 hover:shadow-[0_16px_24px_rgba(16,185,129,0.22)] disabled:cursor-not-allowed disabled:bg-gray-300 disabled:shadow-none"
                        title={!currentScopeAssignment ? 'Select a specific account first' : followerHasCurrentScope(follower) ? 'This user already has access to the current account' : 'Grant the currently selected account to this user'}
                      >
                        Grant Current Account
                      </button>
                      <button
                        onClick={() => {
                          setPendingDeleteUser(follower.email);
                          setIsCheckModalOpen(true);
                        }}
                        className="rounded-xl bg-red-500 px-4 py-2 text-white shadow-[0_10px_20px_rgba(239,68,68,0.16)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-600 hover:shadow-[0_16px_24px_rgba(239,68,68,0.22)]"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                  {(!Array.isArray(follower.assignments) || follower.assignments.length === 0) && (
                    <p className="mt-2 text-[0.85rem] text-amber-600">No account access currently assigned. Select an account on the left, then use "Grant Current Account" to restore access.</p>
                  )}
                  {Array.isArray(follower.assignments) && follower.assignments.length > 0 && (
                    <ul className="mt-2 flex flex-wrap gap-2">
                      {follower.assignments.map((assignment) => {
                        const isCurrentScope = currentScopeAssignment && assignment.targetType === currentScopeAssignment.targetType && assignment.targetId === currentScopeAssignment.targetId;
                        return (
                          <li
                            key={`${assignment.targetType}-${assignment.targetId}`}
                            className="flex items-center gap-2 rounded-full border border-[#d9e2f6] bg-white px-3 py-1 text-[0.8rem] text-[#4d5b75]"
                          >
                            <span>
                              {TARGET_TYPE_LABELS[assignment.targetType] || assignment.targetType}: {assignment.targetName || assignment.targetId}
                            </span>
                            {isCurrentScope && (
                              <button
                                onClick={() => requestRemoveAssignment(follower, assignment)}
                                className="text-[#94a1b8] hover:text-red-500"
                                title="Remove from this account only"
                              >
                                Remove
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              ))
            ) : (
              <span className="text-sm text-gray-500">No user yet</span>
            )}
          </ul>
        </div>
      )}
      <ConfirmModal
        isOpen={isCheckModalOpen}
        onClose={() => {
          setIsCheckModalOpen(false);
        }}
        onConfirm={() => {
          deleteFollower(pendingDeleteUser);
          setIsCheckModalOpen(false);
          setPendingDeleteUser(null);
        }}
        header="Confirm Delete"
        content="Are you sure you want to delete this follower account entirely? This removes all of their access."
      />

      <ConfirmModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setAddChecks({ venue: false, contact: false, email: false });
          setIsAddModalOpen(false);
        }}
        onConfirm={confirmAddFollower}
        header="Confirm Add"
        confirmDisabled={!addConfirmReady}
        content={(
          <div className="space-y-4">
            <p>Please confirm each item before adding this user.</p>
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
              <div><span className="font-semibold">Hotel / Venue:</span> {selectedTargetLabel || '-'}</div>
              <div className="mt-2"><span className="font-semibold">Contact:</span> {trimmedFollowerName || '-'}</div>
              <div><span className="font-semibold">Email:</span> {trimmedFollowerEmail || '-'}</div>
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={addChecks.venue}
                onChange={(e) => setAddChecks((prev) => ({ ...prev, venue: e.target.checked }))}
              />
              <span>I confirm the hotel / venue is correct.</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={addChecks.contact}
                onChange={(e) => setAddChecks((prev) => ({ ...prev, contact: e.target.checked }))}
              />
              <span>I confirm the contact person name is correct.</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={addChecks.email}
                onChange={(e) => setAddChecks((prev) => ({ ...prev, email: e.target.checked }))}
              />
              <span>I confirm the email address is correct.</span>
            </label>
          </div>
        )}
      />

      <ConfirmModal
        isOpen={isGrantAssignmentModalOpen}
        onClose={() => {
          setIsGrantAssignmentModalOpen(false);
          setPendingGrantFollower(null);
        }}
        onConfirm={confirmGrantCurrentAssignment}
        header="Grant current account"
        content={
          pendingGrantFollower && selectedTargetLabel
            ? `Grant ${pendingGrantFollower.email} access to "${selectedTargetLabel}"? Their existing access stays unchanged.`
            : ''
        }
      />

      <ConfirmModal
        isOpen={isRemoveAssignmentModalOpen}
        onClose={() => {
          setIsRemoveAssignmentModalOpen(false);
          setPendingRemoveAssignment(null);
        }}
        onConfirm={confirmRemoveAssignment}
        header="Remove from this account"
        content={
          pendingRemoveAssignment
            ? `Remove ${pendingRemoveAssignment.follower.email} from "${pendingRemoveAssignment.assignment.targetName || pendingRemoveAssignment.assignment.targetId}"? If this is their last assignment, the account will remain in the system but have no access until reassigned.`
            : ''
        }
      />
    </div>
  );
};

export default AddUser;
